import { Navigate, useLocation } from 'react-router-dom'
import { useTenant } from '@/contexts'
import { Loader2 } from 'lucide-react'
import { normalizeSlug } from '@/lib/slugs'

interface LegacyRedirectProps {
  /**
   * The resource suffix to append to the location path.
   * @example 'hangar-view' redirects /operations/hangar-view → /{loc}/hangar-view
   */
  suffix: string
}

/**
 * Redirect component for legacy flat routes.
 *
 * Maintains backward compatibility by redirecting old URLs to the new
 * location-scoped structure.
 *
 * @example
 * // Route definition
 * <Route path="/operations/hangar-view" element={<LegacyRedirect suffix="hangar-view" />} />
 *
 * // User visits /operations/hangar-view
 * // Redirects to /kdet/hangar-view (using first available location)
 */
export function LegacyRedirect({ suffix }: LegacyRedirectProps) {
  const { locations, isLoading } = useTenant()
  const location = useLocation()

  // Show loading while data is being fetched
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // No locations available - go to dashboard
  if (!locations || locations.length === 0) {
    console.warn(`LegacyRedirect: No locations available, redirecting to /dashboard`)
    return <Navigate to="/dashboard" replace />
  }

  // Get the default/first location
  const defaultLocation = locations[0]
  const slug = normalizeSlug(defaultLocation.location_code)

  // Preserve query params from the original URL
  const targetPath = `/${slug}/${suffix}${location.search}`

  console.info(`LegacyRedirect: ${location.pathname} → ${targetPath}`)

  return <Navigate to={targetPath} replace />
}

/**
 * Create a legacy redirect element for use in route definitions.
 * Convenience wrapper for JSX usage.
 */
export function createLegacyRedirect(suffix: string) {
  return <LegacyRedirect suffix={suffix} />
}
